import axios from 'axios';

const getApiBaseUrl = (): string => {
  // Get API URL from window.ENV (loaded from config.js)
  const apiUrl = window.ENV?.VITE_API_BASE_URL;

  if (!apiUrl) {
    console.error('⚠️ window.ENV.VITE_API_BASE_URL is not set in adminService! Check that config.js is loaded properly.');
    console.error('Current window.ENV:', window.ENV);
    return 'http://config.not.loaded.xxx';
  }

  return apiUrl;
};

const API_BASE_URL = getApiBaseUrl();

export interface ChatSession {
  sessionId: string;
  userEmail: string;
  isAnonymous: boolean;
  lastActivity: string;
  messageCount: number;
  hasError: boolean;
  estimatedCostUsd: number;
}

export interface LlmActivity {
  id: number;
  modelId: number;
  modelName: string;
  provider: string;
  systemPrompt: string | null;
  prompt: string;
  responseBody: string | null;
  toolUseRequest: string | null;
  status: string;
  errorMessage: string | null;
  promptTokens: number | null;
  completionTokens: number | null;
  totalTokens: number | null;
  durationMs: number | null;
  estimatedCostUsd: number | null;
}

export interface ChatStep {
  id: number;
  toolUse: string;
  toolQuery: any;
  toolResult: any;
  timestamp: string;
  llmActivityId: number | null;
  estimatedCostUsd: number | null;
  llmActivity?: LlmActivity;
}

export interface ChatMessage {
  id: number;
  sessionId: string;
  role: 'user' | 'assistant';
  message: string;
  searchResults: any[] | null;
  modelName: string | null;
  createdAt: string;
  errorOccurred: boolean;
  errorDetails: string | null;
  userEmail: string;
  isAnonymous: boolean;
  steps: ChatStep[];
}

export interface MonitorSessionsResponse {
  success: boolean;
  sessions: ChatSession[];
  total: number;
  error?: string;
}

export interface SessionHistoryResponse {
  success: boolean;
  sessionId: string;
  messages: ChatMessage[];
  messageCount: number;
  error?: string;
}

export interface LlmProvider {
  provider_id: number;
  provider_name: string;
  display_name: string;
  provider_type: string;
  api_endpoint: string | null;
  api_version: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  api_key_masked?: string;
}

export interface LlmModel {
  id: number;
  model_name: string;
  alias: string | null;
  release: string;
  is_active: boolean;
  is_default: boolean;
  context_window: number | null;
  input_price_per_1m_tokens: number | null;
  output_price_per_1m_tokens: number | null;
  pricing_updated_at: string | null;
  provider_id: number;
  deployment_name: string | null;
  created_at: string;
  updated_at: string;
  provider_display_name?: string;
  provider_type?: string;
}

export interface CreateProviderRequest {
  provider_name: string;
  display_name: string;
  provider_type: string;
  api_key: string;
  api_endpoint?: string;
  api_version?: string;
}

export interface UpdateProviderRequest {
  display_name?: string;
  provider_type?: string;
  api_key?: string;
  api_endpoint?: string;
  api_version?: string;
  is_active?: boolean;
}

export interface CreateModelRequest {
  model_name: string;
  alias?: string;
  release: string;
  context_window?: number;
  input_price_per_1m_tokens?: number;
  output_price_per_1m_tokens?: number;
  provider_id: number;
  deployment_name?: string;
  is_default?: boolean;
}

export interface UpdateModelRequest {
  alias?: string;
  release?: string;
  context_window?: number;
  input_price_per_1m_tokens?: number;
  output_price_per_1m_tokens?: number;
  provider_id?: number;
  deployment_name?: string;
  is_active?: boolean;
  is_default?: boolean;
}

/**
 * Get access token from localStorage
 */
const getAccessToken = (): string | null => {
  return localStorage.getItem('accessToken');
};

export const adminService = {
  /**
   * Fetch all chat sessions for admin monitoring
   */
  getAllSessions: async (): Promise<ChatSession[]> => {
    const token = getAccessToken();

    if (!token) {
      throw new Error('No access token available');
    }

    const response = await axios.get<MonitorSessionsResponse>(
      `${API_BASE_URL}/api/chat/admin/sessions`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch sessions');
    }

    return response.data.sessions;
  },

  /**
   * Fetch complete chat history for a specific session
   */
  getSessionHistory: async (sessionId: string): Promise<ChatMessage[]> => {
    const token = getAccessToken();

    if (!token) {
      throw new Error('No access token available');
    }

    const response = await axios.get<SessionHistoryResponse>(
      `${API_BASE_URL}/api/chat/admin/sessions/${sessionId}/history`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch session history');
    }

    return response.data.messages;
  },

  // ========== LLM Providers ==========

  /**
   * Fetch all LLM providers
   */
  getAllProviders: async (): Promise<LlmProvider[]> => {
    const token = getAccessToken();

    if (!token) {
      throw new Error('No access token available');
    }

    const response = await axios.get<{ providers: LlmProvider[] }>(
      `${API_BASE_URL}/api/chat/admin/llm-providers/list`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data.providers;
  },

  /**
   * Create a new LLM provider
   */
  createProvider: async (data: CreateProviderRequest): Promise<LlmProvider> => {
    const token = getAccessToken();

    if (!token) {
      throw new Error('No access token available');
    }

    const response = await axios.post<{ provider: LlmProvider }>(
      `${API_BASE_URL}/api/chat/admin/llm-providers/create`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data.provider;
  },

  /**
   * Update an existing LLM provider
   */
  updateProvider: async (
    providerId: number,
    data: UpdateProviderRequest
  ): Promise<LlmProvider> => {
    const token = getAccessToken();

    if (!token) {
      throw new Error('No access token available');
    }

    const response = await axios.patch<{ provider: LlmProvider }>(
      `${API_BASE_URL}/api/chat/admin/llm-providers/${providerId}/update`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data.provider;
  },

  /**
   * Delete an LLM provider
   */
  deleteProvider: async (providerId: number): Promise<void> => {
    const token = getAccessToken();

    if (!token) {
      throw new Error('No access token available');
    }

    await axios.delete(`${API_BASE_URL}/api/chat/admin/llm-providers/${providerId}/delete`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  // ========== LLM Models ==========

  /**
   * Fetch all LLM models
   */
  getAllModels: async (): Promise<LlmModel[]> => {
    const token = getAccessToken();

    if (!token) {
      throw new Error('No access token available');
    }

    const response = await axios.get<{ models: LlmModel[] }>(
      `${API_BASE_URL}/api/chat/admin/llm-models/list`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data.models;
  },

  /**
   * Create a new LLM model
   */
  createModel: async (data: CreateModelRequest): Promise<LlmModel> => {
    const token = getAccessToken();

    if (!token) {
      throw new Error('No access token available');
    }

    const response = await axios.post<{ model: LlmModel }>(
      `${API_BASE_URL}/api/chat/admin/llm-models/create`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data.model;
  },

  /**
   * Update an existing LLM model
   */
  updateModel: async (modelId: number, data: UpdateModelRequest): Promise<LlmModel> => {
    const token = getAccessToken();

    if (!token) {
      throw new Error('No access token available');
    }

    const response = await axios.patch<{ model: LlmModel }>(
      `${API_BASE_URL}/api/chat/admin/llm-models/${modelId}/update`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data.model;
  },

  /**
   * Delete an LLM model
   */
  deleteModel: async (modelId: number): Promise<void> => {
    const token = getAccessToken();

    if (!token) {
      throw new Error('No access token available');
    }

    await axios.delete(`${API_BASE_URL}/api/chat/admin/llm-models/${modelId}/delete`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  /**
   * Invalidate the LLM provider cache on the server
   */
  invalidateProviderCache: async (): Promise<void> => {
    const token = getAccessToken();

    if (!token) {
      throw new Error('No access token available');
    }

    await axios.post(
      `${API_BASE_URL}/api/chat/admin/llm-providers/invalidate-cache`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  },
};
